import type { Locale } from "@borealis/layout-express";
import type { IAdViewManaged, ICarUsedAdView } from "@finn-no/adview-provider-frontend-types";
import type { I18n } from "@lingui/core";
import type {
  AgricultureVehicleAd,
  ATractorAd,
  BoatRentAd,
  BoatSaleAd,
  BusAd,
  CarAd,
  CaravanAd,
  ConstructionAd,
  MobileHomeAd,
  MobilityAd,
  MotorbikeAd,
  TruckAd,
} from "@mobility-item-page/types";
import {
  formatMileage,
  formatNumber,
  getValue,
  isAgricultureVehicleAd,
  isATractorAd,
  isBoatRent,
  isBoatSale,
  isBusAd,
  isCarAd,
  isCaravanAd,
  isConstructionAd,
  isMobileHomeAd,
  isMotorbikeAd,
  isTruckAd,
} from "@mobility-item-page/utilities";

import type { IconName } from "../Icon.tsx";

type SummaryItem = {
  icon: IconName;
  label: string;
  value: string | number | null | undefined;
};

type SummaryItemsDeriver<Ad extends MobilityAd> = (ad: Ad, i18n: I18n) => SummaryItem[];

export const deriveSummaryItems: SummaryItemsDeriver<MobilityAd> = (ad, i18n: I18n) => {
  if (isCarAd(ad)) {
    return carSummaryItems(ad, i18n);
  }
  if (isATractorAd(ad)) {
    return aTractorSummaryItems(ad, i18n);
  }
  if (isBoatSale(ad) || isBoatRent(ad)) {
    return boatSummaryItems(ad, i18n);
  }
  if (isMotorbikeAd(ad)) {
    return motorBikeSummaryItems(ad, i18n);
  }
  if (isAgricultureVehicleAd(ad)) {
    return agricultureSummaryItems(ad, i18n);
  }
  if (isMobileHomeAd(ad)) {
    return mobileHomeSummaryItems(ad, i18n);
  }
  if (isCaravanAd(ad)) {
    return caravanSummaryItems(ad, i18n);
  }
  if (isTruckAd(ad)) {
    return truckSummaryItems(ad, i18n);
  }
  if (isConstructionAd(ad)) {
    return constructionSummaryItems(ad, i18n);
  }
  if (isBusAd(ad)) {
    return busSummaryItems(ad, i18n);
  }
  return [];
};

const carSummaryItems: SummaryItemsDeriver<CarAd> = (ad, i18n) => {
  const { year, mileage, transmission, engine, mileage_unit, driving_range } = ad;

  const items: SummaryItem[] = [];

  items.push(yearItem(year, i18n));

  items.push(mileageItem(mileage, mileage_unit, i18n));

  //If Electric
  engine?.fuel?.id === 4
    ? items.push(rangeItem(driving_range, i18n))
    : items.push(transmissionItem(transmission, i18n));

  items.push(fuelItem(engine?.fuel, i18n));

  return items;
};

const aTractorSummaryItems: SummaryItemsDeriver<ATractorAd> = (ad, i18n) => {
  const { year, mileage, transmission, engine, mileage_unit } = ad;

  const items: SummaryItem[] = [];

  items.push(yearItem(year, i18n));

  items.push(mileageItem(mileage, mileage_unit, i18n));

  items.push(transmissionItem(transmission, i18n));

  items.push(fuelItem(engine?.fuel, i18n));

  return items;
};

const boatSummaryItems: SummaryItemsDeriver<BoatSaleAd | BoatRentAd> = (ad, i18n) => {
  const { year, motor, length, no_of_seats } = ad;

  const items: SummaryItem[] = [];

  items.push(yearItem(year, i18n));

  const lengthLabel = i18n.t({
    id: "itemPageLayout.sectionSummary.length",
    message: "Length",
    comment: "Field for 'length' in ad section summary",
  });
  const lengthValue = i18n.t({
    id: "itemPageLayout.common.length.feet.value",
    message: "{length} feet",
    comment: "Field for 'length in feet' in ad specification",
    values: { length: length },
  });
  items.push({
    icon: "Ruler" as IconName,
    label: lengthLabel,
    value: lengthValue,
  });

  const motorLabel = i18n.t({
    id: "itemPageLayout.keyInfo.motor",
    message: "Motor type",
    comment: "Field for 'motor type' in ad specification",
  });
  items.push({
    icon: "Propeller" as IconName,
    label: motorLabel,
    value: motor?.motor_type?.value,
  });

  items.push(noOfSeatsItem(no_of_seats, i18n));

  return items;
};

const motorBikeSummaryItems: SummaryItemsDeriver<MotorbikeAd> = (ad, i18n) => {
  const { year, mileage, engine, mileage_unit } = ad;

  const items: SummaryItem[] = [];

  items.push(yearItem(year, i18n));

  items.push(mileageItem(mileage, mileage_unit, i18n));

  const motorLabel = i18n.t({
    id: "itemPageLayout.sectionSummary.engine.volume",
    message: "Engine displacement",
    comment: "Field for 'engine volume' in ad section summary",
  });
  const formatedVolumeLabel = i18n.t({
    id: "itemPageLayout.common.volume.cc.postfix",
    message: "{volume} cc",
    comment: "Volume in cubic centimetres postfix in ad specification",
    values: {
      volume: formatNumber(engine?.volume, i18n.locale as Locale),
    },
  });
  items.push({
    icon: "EngineBelt" as IconName,
    label: motorLabel,
    value: engine?.volume != null ? formatedVolumeLabel : null,
  });

  items.push(effectItem(engine?.effect, i18n));

  return items;
};

const agricultureSummaryItems: SummaryItemsDeriver<AgricultureVehicleAd> = (ad, i18n) => {
  const items: SummaryItem[] = [];
  const { year, weight, engine, hours_used } = ad;

  items.push(yearItem(year, i18n));

  items.push(weightItem(weight, i18n));

  items.push(effectItem(engine?.effect, i18n));

  items.push(hoursUsedItem(hours_used, i18n));

  return items;
};

const mobileHomeSummaryItems: SummaryItemsDeriver<MobileHomeAd> = (ad, i18n) => {
  const { year, transmission, mileage, engine, mileage_unit } = ad;

  const items: SummaryItem[] = [];

  items.push(yearItem(year, i18n));

  items.push(mileageItem(mileage, mileage_unit, i18n));

  items.push(transmissionItem(transmission, i18n));

  items.push(fuelItem(engine?.fuel, i18n));

  return items;
};

const caravanSummaryItems: SummaryItemsDeriver<CaravanAd> = (ad, i18n) => {
  const { year, weight, length, no_of_sleepers } = ad;

  const items: SummaryItem[] = [];

  items.push(yearItem(year, i18n));

  items.push(weightItem(weight?.total, i18n));

  items.push(lengthCmItem(length?.total, i18n));

  const numberOfSleepersLabel = i18n.t({
    id: "itemPageLayout.common.numberOfSleepers",
    message: "Number of sleepers",
    comment: "Field for 'number of sleepers' in ad specification and summary",
  });
  items.push({
    icon: "DoubleBed" as IconName,
    label: numberOfSleepersLabel,
    value: no_of_sleepers,
  });

  return items;
};

const truckSummaryItems: SummaryItemsDeriver<TruckAd> = (ad, i18n) => {
  const { year, weight, mileage, length, segment, mileage_unit } = ad;

  const items: SummaryItem[] = [];

  items.push(yearItem(year, i18n));

  const isTrailer = segment?.id === 180;

  if (!isTrailer) {
    items.push(mileageItem(mileage, mileage_unit, i18n));
  }

  items.push(weightItem(weight?.total, i18n));

  items.push(lengthCmItem(length, i18n));

  return items;
};

const constructionSummaryItems: SummaryItemsDeriver<ConstructionAd> = (ad, i18n) => {
  const { year, ce_marked, hours_used, weight } = ad;

  const items: SummaryItem[] = [];

  items.push(yearItem(year, i18n));

  const ceMarkedLabel = i18n._({
    id: "itemPageLayout.keyInfo.ceMarked",
    message: "CE-marked",
    comment: "Label for 'CE-marked' in ad summary section",
  });
  const yesText = i18n.t({
    id: "itemPageLayout.keyInfo.yes",
    message: "Yes",
    comment: "Field for 'yes' in ad summary section",
  });
  const noText = i18n.t({
    id: "itemPageLayout.keyInfo.no",
    message: "No",
    comment: "Field for 'no' in ad summary section",
  });
  items.push({
    icon: "CheckShield" as IconName,
    label: ceMarkedLabel,
    value: typeof ce_marked === "boolean" ? (ce_marked ? yesText : noText) : null,
  });
  items.push(hoursUsedItem(hours_used, i18n));

  items.push(weightItem(weight, i18n));

  return items;
};

const busSummaryItems: SummaryItemsDeriver<BusAd> = (ad, i18n) => {
  const { year, mileage, no_of_seats, mileage_unit } = ad;

  const items: SummaryItem[] = [];

  items.push(yearItem(year, i18n));

  items.push(mileageItem(mileage, mileage_unit, i18n));

  items.push(noOfSeatsItem(no_of_seats, i18n));

  return items;
};

const yearItem = (year: number | undefined, i18n: I18n) => {
  return {
    icon: "Calendar" as IconName,
    label: i18n.t({
      id: "itemPageLayout.common.year",
      message: "Model year",
      comment: "Label for production year in ad section summary",
    }),
    value: year?.toString(),
  };
};

/**
 * The mileage unit type is shared across multiple ad types, so we reuse the type from ICarUsedAdView.
 */
type MileageUnit = ICarUsedAdView["mileage_unit"];

const mileageItem = (mileage: number | undefined, unit: MileageUnit, i18n: I18n) => {
  const mileageLabel = i18n._({
    id: "itemPageLayout.sectionSummary.mileage",
    message: "Mileage",
    comment: "Label for mileage in ad section summary",
  });
  return {
    icon: "Speedometer" as IconName,
    label: mileageLabel,
    value: mileage != null ? formatMileage(mileage, unit, i18n.locale) : null,
  };
};

const transmissionItem = (transmission: IAdViewManaged | undefined, i18n: I18n) => {
  const transmissionLabel = i18n.t({
    id: "itemPageLayout.common.transmission.label",
    message: "Transmission",
    comment: "'Transmission' label",
  });
  if (transmission) {
    const isAutomatic = transmission.id === 2;
    return {
      icon: isAutomatic ? ("GearAutomatic" as IconName) : ("GearManual" as IconName),
      label: transmissionLabel,
      value: getValue(transmission),
    };
  } else {
    return {
      icon: "GearManual" as IconName,
      label: transmissionLabel,
      value: null,
    };
  }
};

const rangeItem = (range: number | undefined, i18n: I18n) => {
  const rangeLabel = i18n.t({
    id: "itemPageLayout.keyInfo.driving_range",
    message: "Driving range",
    comment: "Field for 'driving range' in ad specification",
  });
  const rangeValue = range
    ? i18n.t({
        id: "itemPageLayout.common.distance.kilometer.postfix",
        message: "{distance} km",
        comment: "Distance incl. postfix",
        values: { distance: formatNumber(range, i18n.locale as Locale) },
      })
    : undefined;
  return {
    icon: "Road" as IconName,
    label: rangeLabel,
    value: rangeValue,
  };
};

const fuelItem = (fuel: IAdViewManaged | undefined, i18n: I18n) => {
  const fuelLabel = i18n.t({
    id: "itemPageLayout.common.fuel.label",
    message: "Fuel",
    comment: "'Fuel' label in ad section summary",
  });
  if (fuel) {
    return {
      icon: getFuelIcon(fuel) as IconName,
      label: fuelLabel,
      value: fuel.value,
    };
  } else {
    return {
      icon: "GasDiesel" as IconName,
      label: fuelLabel,
      value: null,
    };
  }
};

const weightItem = (weight: number | undefined, i18n: I18n) => {
  const weightLabel = i18n.t({
    id: "itemPageLayout.sectionSummary.weight",
    message: "Weight",
    comment: "Field for 'weight' in ad summary",
  });
  const weightFormatted = i18n.t({
    id: "itemPageLayout.common.weight.postfix",
    message: "{weight} kg",
    comment: "Weight incl. postfix",
    values: {
      weight: formatNumber(weight, i18n.locale as Locale),
    },
  });

  return {
    icon: "Weight" as IconName,
    label: weightLabel,
    value: weight ? weightFormatted : null,
  };
};

const lengthCmItem = (length: number | undefined, i18n: I18n) => {
  const lengthLabel = i18n.t({
    id: "itemPageLayout.sectionSummary.length",
    message: "Length",
    comment: "Field for 'length' in ad section summary",
  });
  const lengthFormatted = i18n.t({
    id: "itemPageLayout.common.length.value",
    message: "{length} cm",
    comment: "Length incl. postfix in cm",
    values: { length: formatNumber(length, i18n.locale as Locale) },
  });
  return {
    icon: "Ruler" as IconName,
    label: lengthLabel,
    value: length ? lengthFormatted : null,
  };
};

const effectItem = (effect: number | undefined, i18n: I18n) => {
  const effectLabel = i18n.t({
    id: "itemPageLayout.sectionSummary.engine",
    message: "Effect",
    comment: "Field for 'engine' in ad summary",
  });
  const effectFormatted = i18n.t({
    id: "itemPageLayout.common.engine.effect.postfix",
    message: "{effect} Hp",
    comment: "Engine effect incl. postfix",
    values: {
      effect: formatNumber(effect, i18n.locale as Locale),
    },
  });
  return {
    icon: "Engine" as IconName,
    label: effectLabel,
    value: effect ? effectFormatted : null,
  };
};

const hoursUsedItem = (hoursUsed: number | undefined, i18n: I18n) => {
  const hoursUsedLabel = i18n.t({
    id: "itemPageLayout.keyInfo.hoursUsed",
    message: "Hours used",
    comment: "Field for 'hours used' in ad summary",
  });
  return {
    icon: "Clock" as IconName,
    label: hoursUsedLabel,
    value: formatNumber(hoursUsed, i18n.locale as Locale),
  };
};

const noOfSeatsItem = (noOfSeats: number | undefined, i18n: I18n) => {
  const seatsLabel = i18n.t({
    id: "itemPageLayout.keyInfo.common.noOfSeats",
    message: "Number of seats",
    comment: "Field for 'number of seats' in ad section summary",
  });
  return {
    icon: "Seat" as IconName,
    label: seatsLabel,
    value: noOfSeats,
  };
};

const getFuelIcon = (fuel: IAdViewManaged) => {
  switch (fuel.id) {
    // Petrol
    // Diesel
    // Hydrogen
    case 1:
    case 2:
    case 9:
      return "GasDiesel";
    // LPG
    case 3:
      return "GasFuel";
    // Electrical
    case 4:
      return "Charger";
    // LPG and gasoline
    // Electrical and gasoline
    // LPG and diesel
    // Electrical and diesel
    // Plugin gasoline
    // Plugin diesel
    case 5:
    case 6:
    case 7:
    case 8:
    case 1352:
    case 1356:
      return "GasHybrid";
    default:
      return "GasFuel";
  }
};
